import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, FormGroup } from '@angular/forms';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { JsonObjectFactoryService } from '../../shared/_services/json-object-factory.service';
import { ScenarioSetupService } from './scenario-setup.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import { shiftIsoDateMonths, toIsoDateString, toUsDateString } from '../../shared/_utils/date-utils';

@Component({
    selector: 'gtw-scenario-setup',
    templateUrl: './scenario-setup.component.html',
    styleUrls: ['./scenario-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ScenarioSetupComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  rowData: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  // Component properties
  modalTitle = "Scenario Setup";
  showEntityLoader = false;
  isSaving = false;
  isSavingUpdate = false;
  isSavingDelete = false;
  crudLoading = false;
  dateOptions = {};
  currentDate = new Date();
  isUpdateMode = false;
  minimise = false;
  private modalCallbackName = '';
  private lastInitKey = '';
  
  // Form data
  scenarioEntity: any = {
    TAX_YEAR: '',
    FILING_LE_ID: '',
    PARENT_LE_ID: '',
    GROUP_OBJ_KEY: '',
    GROUP_OBJ_KEY_898: '',
    TRADITIONAL_START_DATE: '',
    TAX_YEAR_START_DATE: '',
    TAX_YEAR_END_DATE: '',
    BATCH_START_DATE: '',
    BATCH_END_DATE: '',
    VIEW_START_DATE: '',
    VIEW_END_DATE: '',
    OWNERSHIP_TYPE: '',
    COMMENTS: '',
    IRS_FORM_TY_BEGIN_DATE: '',
    IRS_FORM_TY_END_DATE: '',
    APP_SCENARIO: '',
    SCENARIO_EC: '',
    SCENARIO_TYPE_CODE: '',
    SCENARIO_DESC: '',
    CONFIG_VALUE: '',
    CLIENT_ID: '',
    SCENARIO: ''
  };

  scenarioEntityBackup: any = {};

  // Dropdown data
  allscenarioTypeCode: any[] = [];
  allscenarioDesc: any[] = [];
  allClientId: any[] = [];
  allGroupObjByYear: any[] = [];
  allGroupObjByYear898: any[] = [];
  allLeids: any[] = [];

  // Global services
  globalService: any;
  GENERAL_CONFIG: any;
  USER_SETTINGS: any;

  constructor(
    private fb: UntypedFormBuilder,
    private _snackBar: MatSnackBar,
    private jsonObjectFactoryService: JsonObjectFactoryService,
    private scenarioSetupService: ScenarioSetupService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.checkAndInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.checkAndInit();
    }));
    this.checkAndInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.modalCallbackName =
      activeScreen?.data?.modalName
      || activeScreen?.data?.modal_name
      || activeScreen?.data?.colData?.call_back
      || '';
    this.rowData = this.resolveScenarioRowData(activeScreen);
    this.cdr.markForCheck();
  }

  /** Grid Actions "Create" still passes the selected row — use callback name for mode. */
  private resolveIsUpdateMode(): boolean {
    const callback = this.modalCallbackName.toLowerCase();
    if (callback.includes('create')) {
      return false;
    }
    if (callback.includes('update')) {
      return true;
    }
    return this.hasScenarioRowData(this.rowData);
  }

  private hasScenarioRowData(data: any): boolean {
    if (!data || Array.isArray(data) || typeof data !== 'object') {
      return false;
    }
    return (data.SCENARIO != null && data.SCENARIO !== '')
      || (data.FILING_LE_ID != null && data.FILING_LE_ID !== '')
      || (data.APP_SCENARIO != null && data.APP_SCENARIO !== '');
  }

  /** Legacy update controller reads `gridData.rowData`; grid batch actions use `{ data }`. */
  private resolveScenarioRowData(activeScreen: any): any {
    const activeData = activeScreen?.data || {};
    const gridPayload = gridPayloadFromActiveScreen(activeScreen);

    if (gridPayload && typeof gridPayload === 'object' && !Array.isArray(gridPayload) && gridPayload.rowData) {
      return gridPayload.rowData;
    }
    if (Array.isArray(gridPayload) && gridPayload[0]?.data) {
      return gridPayload[0].data;
    }

    const raw = activeData?.selected?.rowData ?? activeData?.rowData;
    if (Array.isArray(raw) && raw[0]?.data) {
      return raw[0].data;
    }
    if (this.hasScenarioRowData(raw)) {
      return raw;
    }

    const fromPayload = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(fromPayload) && fromPayload[0]?.data) {
      return fromPayload[0].data;
    }
    if (this.hasScenarioRowData(fromPayload)) {
      return fromPayload;
    }

    return {};
  }

  // Check if all required inputs are available before initializing
  checkAndInit(): void {
    // Wait for all required inputs to be available
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    const hasBaseUrls = this.baseURLs && Object.keys(this.baseURLs).length > 0;
    const hasHeaderData = this.globalService && this.GENERAL_CONFIG;
    
    // Only initialize when we have the minimum required inputs
    if (hasFilterParams && hasBaseUrls && hasHeaderData) {
      const currentInitKey = this.buildInitKey();
      if (this.lastInitKey === currentInitKey) {
        return;
      }
      this.lastInitKey = currentInitKey;
      this.init();
    }
  }

  init(): void {
    // Load initial data for dropdowns
    this.loadInitialData();
    
    // Mode from grid callback (createScenarioSetup vs updateScenarioSetup), not row presence alone.
    if (this.resolveIsUpdateMode()) {
      this.isUpdateMode = true;
      this.scenarioEntity = this.normalizeUpdateRowData(this.rowData);
      this.normalizeUpdateDates(this.scenarioEntity);
      this.scenarioEntityBackup = _.cloneDeep(this.scenarioEntity);
      this.hydrateMissingUpdateFields();
    } else {
      this.isUpdateMode = false;
      this.initializeCreateMode();
    }
  }

  private buildInitKey(): string {
    const mode = this.resolveIsUpdateMode() ? 'update' : 'create';
    const taxYear = String(this.filterParams?.tax_year ?? this.rowData?.TAX_YEAR ?? '');
    const scenario = String(this.rowData?.SCENARIO ?? '');
    const appScenario = String(this.rowData?.APP_SCENARIO ?? '');
    return `${mode}|${taxYear}|${scenario}|${appScenario}|${this.modalCallbackName}`;
  }

  private normalizeUpdateRowData(source: any): any {
    const normalized = _.cloneDeep(source || {});
    const configValue = normalized.CONFIG_VALUE ?? normalized.CLIENT_ID ?? '';
    const clientId = normalized.CLIENT_ID ?? normalized.CONFIG_VALUE ?? '';
    normalized.CONFIG_VALUE = configValue;
    normalized.CLIENT_ID = clientId;
    normalized.OWNERSHIP_TYPE = `${normalized.OWNERSHIP_TYPE ?? ''}`;
    return normalized;
  }

  private normalizeUpdateDates(entity: any): void {
    entity.TAX_YEAR_START_DATE = toIsoDateString(entity.TAX_YEAR_START_DATE);
    entity.TAX_YEAR_END_DATE = toIsoDateString(entity.TAX_YEAR_END_DATE);
    entity.TRADITIONAL_START_DATE = toIsoDateString(entity.TRADITIONAL_START_DATE);
    entity.BATCH_START_DATE = toIsoDateString(entity.BATCH_START_DATE);
    entity.BATCH_END_DATE = toIsoDateString(entity.BATCH_END_DATE);
    entity.VIEW_START_DATE = toIsoDateString(entity.VIEW_START_DATE);
    entity.VIEW_END_DATE = toIsoDateString(entity.VIEW_END_DATE);
    entity.IRS_FORM_TY_BEGIN_DATE = toIsoDateString(entity.IRS_FORM_TY_BEGIN_DATE);
    entity.IRS_FORM_TY_END_DATE = toIsoDateString(entity.IRS_FORM_TY_END_DATE);
  }

  private needsUpdateHydration(entity: any): boolean {
    const hasConfigValue = entity?.CONFIG_VALUE != null && entity.CONFIG_VALUE !== '';
    const hasGroupObj898 = entity?.GROUP_OBJ_KEY_898 != null && entity.GROUP_OBJ_KEY_898 !== '';
    return !hasConfigValue || !hasGroupObj898;
  }

  private hydrateMissingUpdateFields(): void {
    if (!this.needsUpdateHydration(this.scenarioEntity)) {
      return;
    }

    this.scenarioSetupService.loadScenarioByYear(this.filterParams, this.rowData).subscribe(
      (rows: any[]) => {
        const hydrated = this.findMatchingUpdateRow(rows);
        if (!hydrated) {
          return;
        }

        const hydratedNormalized = this.normalizeUpdateRowData(hydrated);
        const merged = { ...hydratedNormalized, ...this.scenarioEntity };

        if (!merged.CONFIG_VALUE && hydratedNormalized.CONFIG_VALUE) {
          merged.CONFIG_VALUE = hydratedNormalized.CONFIG_VALUE;
        }
        if (!merged.CLIENT_ID && hydratedNormalized.CLIENT_ID) {
          merged.CLIENT_ID = hydratedNormalized.CLIENT_ID;
        }
        if ((merged.GROUP_OBJ_KEY_898 == null || merged.GROUP_OBJ_KEY_898 === '')
          && (hydratedNormalized.GROUP_OBJ_KEY_898 != null && hydratedNormalized.GROUP_OBJ_KEY_898 !== '')) {
          merged.GROUP_OBJ_KEY_898 = hydratedNormalized.GROUP_OBJ_KEY_898;
        }

        this.normalizeUpdateDates(merged);
        this.scenarioEntity = merged;
        this.scenarioEntityBackup = _.cloneDeep(merged);
        this.cdr.markForCheck();
      },
      () => {
        // Best-effort hydration only; keep already loaded row data.
      }
    );
  }

  private findMatchingUpdateRow(rows: any[]): any | null {
    if (!Array.isArray(rows) || rows.length === 0) {
      return null;
    }

    const targetScenario = String(this.scenarioEntity?.SCENARIO ?? this.rowData?.SCENARIO ?? '');
    const targetAppScenario = String(this.scenarioEntity?.APP_SCENARIO ?? this.rowData?.APP_SCENARIO ?? '');
    const targetTaxYear = String(this.scenarioEntity?.TAX_YEAR ?? this.filterParams?.tax_year ?? this.rowData?.TAX_YEAR ?? '');

    return rows.find((item: any) => {
      const sameTaxYear = String(item?.TAX_YEAR ?? '') === targetTaxYear;
      const sameScenario = targetScenario !== '' && String(item?.SCENARIO ?? '') === targetScenario;
      const sameAppScenario = targetAppScenario !== '' && String(item?.APP_SCENARIO ?? '') === targetAppScenario;
      return sameTaxYear && (sameScenario || sameAppScenario);
    }) || rows[0];
  }

  initializeCreateMode(): void {
    const taxYear = String(this.filterParams.tax_year || '');
    const priorYear = String(parseInt(taxYear, 10) - 1);
    this.scenarioEntity.TAX_YEAR = taxYear;
    this.scenarioEntity.TAX_YEAR_START_DATE = `${taxYear}-01-01`;
    this.scenarioEntity.TAX_YEAR_END_DATE = `${taxYear}-12-31`;
    this.scenarioEntity.TRADITIONAL_START_DATE = `${priorYear}-12-01`;
    this.scenarioEntity.BATCH_START_DATE = `${taxYear}-01-01`;
    this.scenarioEntity.VIEW_START_DATE = `${taxYear}-01-01`;
    this.scenarioEntity.IRS_FORM_TY_BEGIN_DATE = `${taxYear}-01-01`;
    this.scenarioEntity.IRS_FORM_TY_END_DATE = `${taxYear}-12-31`;
    this.scenarioEntity.OWNERSHIP_TYPE = "VALUE";
    this.scenarioEntity.SCENARIO_EC = "1";
    this.scenarioEntity.SCENARIO_TYPE_CODE = '';
    this.scenarioEntity.SCENARIO_DESC = '';
    this.scenarioEntity.GROUP_OBJ_KEY = '';
    this.scenarioEntity.GROUP_OBJ_KEY_898 = '';
    this.scenarioEntity.CONFIG_VALUE = '';
    this.scenarioEntity.PARENT_LE_ID = '';
  }

  convertToDateInputFormat(date: any): string {
    return toIsoDateString(date);
  }

  convertFromDateInputFormat(dateString: string): string {
    return toUsDateString(dateString);
  }

  loadInitialData(): void {
    this.showEntityLoader = true;
    
    // Load scenario type codes
    this.scenarioSetupService.loadScenarioTypeCode(this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.allscenarioTypeCode = data;
        this.cdr.markForCheck();
      }
    );

    // Load client IDs
    this.scenarioSetupService.loadClientId(this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.allClientId = data;
        this.cdr.markForCheck();
      }
    );

    this.showEntityLoader = false;
  }

  onScenarioTypeCodeChange(): void {
    this.scenarioEntity.SCENARIO_DESC = '';
    this.scenarioEntity.GROUP_OBJ_KEY = '';
    this.scenarioEntity.GROUP_OBJ_KEY_898 = '';
    this.allscenarioDesc = [];
    this.allGroupObjByYear = [];
    this.allGroupObjByYear898 = [];
    this.allLeids = [];

    if (this.scenarioEntity.SCENARIO_TYPE_CODE) {
      this.scenarioSetupService.loadScenarioDesc(this.scenarioEntity.SCENARIO_TYPE_CODE, this.filterParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.allscenarioDesc = data;
          this.cdr.markForCheck();
        }
      );
    }
    this.cdr.markForCheck();
  }

  onPopulateGroupObjYear(): void {
    this.scenarioEntity.GROUP_OBJ_KEY = '';
    this.scenarioEntity.GROUP_OBJ_KEY_898 = '';
    this.allGroupObjByYear = [];
    this.allGroupObjByYear898 = [];
    this.allLeids = [];

    if (this.scenarioEntity.SCENARIO_TYPE_CODE && this.scenarioEntity.SCENARIO_DESC) {
      // Load group obj key for current year
      this.scenarioSetupService.loadGroupObjKey(
        this.scenarioEntity.SCENARIO_TYPE_CODE, 
        this.scenarioEntity.SCENARIO_DESC,
        this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.allGroupObjByYear = data;
          this.cdr.markForCheck();
        }
      );

      // Load group obj key for 898 year
      this.scenarioSetupService.loadGroupObjKey898(
        this.scenarioEntity.SCENARIO_TYPE_CODE, 
        this.scenarioEntity.SCENARIO_DESC, this.filterParams
      ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.allGroupObjByYear898 = data;
          this.cdr.markForCheck();
        }
      );
    }
  }

  onPopulateFilingLEID(): void {
    if (this.scenarioEntity.GROUP_OBJ_KEY) {
      this.scenarioSetupService.loadAllEntities(this.scenarioEntity.GROUP_OBJ_KEY).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
        (data: any) => {
          this.allLeids = data;
          this.cdr.markForCheck();
        }
      );
    }
  }

  onPopulateOwnershipType(): void {
    if (this.scenarioEntity.SCENARIO_EC == "1") {
      this.scenarioEntity.OWNERSHIP_TYPE = "VALUE";
    } else {
      this.scenarioEntity.OWNERSHIP_TYPE = "VOTE";
    }
  }

  onPopulateTraditionStartDate(): void {
    if (this.scenarioEntity.TAX_YEAR_START_DATE) {
      this.scenarioEntity.TRADITIONAL_START_DATE = shiftIsoDateMonths(
        this.scenarioEntity.TAX_YEAR_START_DATE,
        -1
      );
    }
  }

  isValid(): boolean {
    let requiredFields: string[] = [];
    
    if (this.isUpdateMode) {
      requiredFields = [
        'TAX_YEAR', 'FILING_LE_ID', 'GROUP_OBJ_KEY_898', 'TRADITIONAL_START_DATE',
        'TAX_YEAR_START_DATE', 'TAX_YEAR_END_DATE', 'BATCH_START_DATE', 'VIEW_START_DATE'
      ];
    } else {
      requiredFields = [
        'TAX_YEAR', 'SCENARIO_TYPE_CODE', 'SCENARIO_DESC', 'CONFIG_VALUE',
        'GROUP_OBJ_KEY', 'PARENT_LE_ID', 'TRADITIONAL_START_DATE',
        'TAX_YEAR_START_DATE', 'TAX_YEAR_END_DATE', 'SCENARIO_EC',
        'BATCH_START_DATE', 'VIEW_START_DATE'
      ];
    }

    for (let field of requiredFields) {
      if (!this.scenarioEntity[field]) {
        this.scenarioEntity[field + '_NOT_VALID'] = true;
        return false;
      }
    }
    return true;
  }

  save(): void {
    if (!this.isValid()) {
      this.gtwSnackbarService.openSnackBar('Please fill all the required fields', 'gtw-snackbar--red');
      return;
    }

    if (this.isUpdateMode) {
      this.updateScenario();
    } else {
      this.createScenario();
    }
  }

  createScenario(): void {
    this.crudLoading = true;
    
    // Build save data with real filterParams values
    const saveData = this.buildSaveData();
    
    // Pass filterParams and rowData to service
    this.scenarioSetupService.saveScenarioSetupForm(
      this.GENERAL_CONFIG.base_url,
      saveData,
      this.globalService,
      this.filterParams,
      this.scenarioEntity
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar('Scenario Details created successfully', 'gtw-snackbar--green');
          
          // Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  updateScenario(): void {
    this.isSavingUpdate = true;
    
    // Build update data
    const updateData = this.buildUpdateData();
    
    // Pass filterParams and rowData to service
    this.scenarioSetupService.updateScenarioSetupForm(
      this.GENERAL_CONFIG.base_url,
      updateData,
      this.globalService,
      this.filterParams,
      this.scenarioEntity
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSavingUpdate = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar('Scenario Details updated successfully', 'gtw-snackbar--green');
          
          // Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSavingUpdate = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while updating. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  deleteScenario(): void {
    this.isSavingDelete = true;
    
    this.scenarioSetupService.deleteScenarioSetupForm(
      this.GENERAL_CONFIG.base_url,
      this.scenarioEntity.TAX_YEAR,
      this.scenarioEntity.SCENARIO,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSavingDelete = false;
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // Show snackBar and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar('Scenario deleted successfully', 'gtw-snackbar--green');
          
          // Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.isSavingDelete = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  buildSaveData(): any {
    let jsonObj = _.merge({}, this.scenarioEntity, this.filterParams);
    
    // Convert dates from HTML5 format back to MM/DD/YYYY for API
    jsonObj.TAX_YEAR_START_DATE = this.convertFromDateInputFormat(jsonObj.TAX_YEAR_START_DATE);
    jsonObj.TAX_YEAR_END_DATE = this.convertFromDateInputFormat(jsonObj.TAX_YEAR_END_DATE);
    jsonObj.TRADITIONAL_START_DATE = this.convertFromDateInputFormat(jsonObj.TRADITIONAL_START_DATE);
    jsonObj.BATCH_START_DATE = this.convertFromDateInputFormat(jsonObj.BATCH_START_DATE);
    jsonObj.BATCH_END_DATE = this.convertFromDateInputFormat(jsonObj.BATCH_END_DATE);
    jsonObj.VIEW_START_DATE = this.convertFromDateInputFormat(jsonObj.VIEW_START_DATE);
    jsonObj.VIEW_END_DATE = this.convertFromDateInputFormat(jsonObj.VIEW_END_DATE);
    jsonObj.IRS_FORM_TY_BEGIN_DATE = this.convertFromDateInputFormat(jsonObj.IRS_FORM_TY_BEGIN_DATE);
    jsonObj.IRS_FORM_TY_END_DATE = this.convertFromDateInputFormat(jsonObj.IRS_FORM_TY_END_DATE);
    
    return this.scenarioSetupService.convertDateFormats(jsonObj);
  }

  buildUpdateData(): any {
    let jsonObj = _.merge({}, this.scenarioEntity, this.filterParams);
    
    // Convert dates from HTML5 format back to MM/DD/YYYY for API
    jsonObj.TAX_YEAR_START_DATE = this.convertFromDateInputFormat(jsonObj.TAX_YEAR_START_DATE);
    jsonObj.TAX_YEAR_END_DATE = this.convertFromDateInputFormat(jsonObj.TAX_YEAR_END_DATE);
    jsonObj.TRADITIONAL_START_DATE = this.convertFromDateInputFormat(jsonObj.TRADITIONAL_START_DATE);
    jsonObj.BATCH_START_DATE = this.convertFromDateInputFormat(jsonObj.BATCH_START_DATE);
    jsonObj.BATCH_END_DATE = this.convertFromDateInputFormat(jsonObj.BATCH_END_DATE);
    jsonObj.VIEW_START_DATE = this.convertFromDateInputFormat(jsonObj.VIEW_START_DATE);
    jsonObj.VIEW_END_DATE = this.convertFromDateInputFormat(jsonObj.VIEW_END_DATE);
    jsonObj.IRS_FORM_TY_BEGIN_DATE = this.convertFromDateInputFormat(jsonObj.IRS_FORM_TY_BEGIN_DATE);
    jsonObj.IRS_FORM_TY_END_DATE = this.convertFromDateInputFormat(jsonObj.IRS_FORM_TY_END_DATE);
    
    return this.scenarioSetupService.convertDateFormats(jsonObj);
  }

  reset(): void {
    if (this.isUpdateMode) {
      this.scenarioEntity = _.cloneDeep(this.scenarioEntityBackup);
    } else {
      this.scenarioEntity = {
        TAX_YEAR: this.filterParams.tax_year || '',
        FILING_LE_ID: '',
        PARENT_LE_ID: '',
        GROUP_OBJ_KEY: '',
        GROUP_OBJ_KEY_898: '',
        TRADITIONAL_START_DATE: '',
        TAX_YEAR_START_DATE: '',
        TAX_YEAR_END_DATE: '',
        BATCH_START_DATE: '',
        BATCH_END_DATE: '',
        VIEW_START_DATE: '',
        VIEW_END_DATE: '',
        OWNERSHIP_TYPE: 'VALUE',
        COMMENTS: '',
        IRS_FORM_TY_BEGIN_DATE: '',
        IRS_FORM_TY_END_DATE: '',
        APP_SCENARIO: '',
        SCENARIO_EC: '1',
        SCENARIO_TYPE_CODE: '',
        SCENARIO_DESC: '',
        CONFIG_VALUE: '',
        CLIENT_ID: '',
        SCENARIO: ''
      };
      this.initializeCreateMode();
      this.allscenarioDesc = [];
      this.allGroupObjByYear = [];
      this.allGroupObjByYear898 = [];
      this.allLeids = [];
    }
  }

  cancel(): void {
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-scenario-setup-ce')) {
      document.querySelector('gtw-scenario-setup-ce')?.dispatchEvent(updateEvent);
    }
    // AngularJS handles modal close automatically via event listener
  }
}
